import { DatePicker, DateTimePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { Placement } from '@popperjs/core'
import { isValid, parse, parseISO } from 'date-fns'
import { useRef } from 'react'
import CalendarIcon from '../../Assets/Images/svgs/calenderIcon'
import { CONSTANTS } from '../../Constants/Constant'

const MUIDateTimePicker = ({
  label,
  type,
  onChange,
  value,
  name,
  error,
  required = false,
  views,
  minutesStep = 5,
  disablePast = false,
  placement,
}: {
  label: string
  type: string
  onChange: any
  value: any
  name: any
  error: any
  required?: boolean
  views?: any
  minutesStep?: number
  disablePast?: boolean
  placement?: Placement
}) => {
  const dateRef: React.Ref<HTMLInputElement> = useRef(null)

  const handlePaste = (
    event: React.ClipboardEvent<HTMLInputElement>,
    format: string,
    alternateFormats: string[] = [],
  ) => {
    const pastedValue = event.clipboardData.getData('Text')?.trim() || ''
    if (!pastedValue) return

    const parsedDates = [
      parse(pastedValue, format, new Date()),
      ...alternateFormats.map((dateFormat) => parse(pastedValue, dateFormat, new Date())),
    ]

    if (pastedValue.includes('T')) {
      parsedDates.push(parseISO(pastedValue))
    }

    const validParsedDate = parsedDates.find((date) => isValid(date))
    if (!validParsedDate) return

    event.preventDefault()
    onChange(validParsedDate)
  }

  return (
    <>
      {type === 'year' && (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            slots={{
              openPickerIcon: CalendarIcon,
            }}
            format={CONSTANTS.DATE_YEAR_FORMAT}
            inputRef={dateRef}
            label={
              <span>
                {label}
                {required && <span style={{ color: '#D4351C' }}>*</span>}
              </span>
            }
            sx={{ width: '100%' }}
            onChange={onChange}
            value={value}
            views={views}
            disablePast={disablePast}
            slotProps={{
              textField: {
                error: !!error?.[name],
                inputProps: {
                  id: name,
                },
                onPaste: (e: React.ClipboardEvent<HTMLInputElement>) =>
                  handlePaste(e, CONSTANTS.DATE_YEAR_FORMAT),
                helperText: error?.[name]?.message as string,
                onFocus: () => {
                  if (dateRef?.current?.click) dateRef.current.click()
                },
              },
              field: { clearable: true, onClear: () => onChange(null) },
              popper: { sx: { zIndex: 1301 }, ...(placement && { placement }) },
            }}
          />
        </LocalizationProvider>
      )}
      {type === 'date' && (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            slots={{
              openPickerIcon: CalendarIcon,
            }}
            inputRef={dateRef}
            format={CONSTANTS.DATE_FORMAT}
            disablePast={disablePast}
            label={
              <span>
                {label}
                {required && <span style={{ color: '#D4351C' }}>*</span>}
              </span>
            }
            sx={{ width: '100%' }}
            onChange={onChange}
            value={value}
            slotProps={{
              textField: {
                error: !!error?.[name],
                inputProps: { id: name },
                onPaste: (e: React.ClipboardEvent<HTMLInputElement>) =>
                  handlePaste(e, CONSTANTS.DATE_FORMAT),
                helperText: error?.[name]?.message as string,
                onFocus: () => {
                  if (dateRef?.current?.click) dateRef.current.click()
                },
                onBlur: (ev) => {
                  if (ev?.target?.value?.includes('0000')) {
                    onChange(null)
                  }
                },
              },
              field: { clearable: true, onClear: () => onChange(null) },
              popper: { sx: { zIndex: 1301 }, ...(placement && { placement }) },
            }}
          />
        </LocalizationProvider>
      )}
      {type === 'time' && (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <TimePicker
            slots={{
              openPickerIcon: CalendarIcon,
            }}
            inputRef={dateRef}
            disablePast={disablePast}
            views={views}
            label={
              <span>
                {label}
                {required && <span style={{ color: '#D4351C' }}>*</span>}
              </span>
            }
            format={CONSTANTS.TIME_FORMAT_3}
            ampm={false}
            sx={{ width: '100%' }}
            onChange={onChange}
            value={value}
            slotProps={{
              textField: {
                error: !!error?.[name],
                inputProps: { id: name },
                onPaste: (e: React.ClipboardEvent<HTMLInputElement>) =>
                  handlePaste(e, CONSTANTS.TIME_FORMAT_3, ['HH:mm', 'HH:mm:ss']),
                helperText: error?.[name]?.message as string,
                onFocus: () => {
                  if (dateRef?.current?.click) dateRef.current.click()
                },
                onBlur: (ev) => {
                  if (ev?.target?.value?.includes('0000')) {
                    onChange(null)
                  }
                },
              },
              popper: { sx: { zIndex: 1301 }, ...(placement && { placement }) },
            }}
          />
        </LocalizationProvider>
      )}
      {type === 'datetime' && (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateTimePicker
            inputRef={dateRef}
            timeSteps={{ minutes: minutesStep }}
            views={views}
            disablePast={disablePast}
            slots={{
              openPickerIcon: CalendarIcon,
            }}
            label={
              <span>
                {label}
                {required && <span style={{ color: '#D4351C' }}>*</span>}
              </span>
            }
            format={CONSTANTS.TIME_FORMAT_3}
            ampm={false}
            sx={{ width: '100%' }}
            onChange={onChange}
            value={value}
            slotProps={{
              textField: {
                error: !!error?.[name],
                inputProps: { id: name },
                onPaste: (e: React.ClipboardEvent<HTMLInputElement>) =>
                  handlePaste(e, CONSTANTS.TIME_FORMAT_3, [
                    CONSTANTS.DATE_FORMAT_TIME,
                    CONSTANTS.DATE_YEAR_FORMAT_TIME,
                    'yyyy-MM-dd HH:mm',
                    'yyyy-MM-dd\'T\'HH:mm:ss',
                    'yyyy-MM-dd\'T\'HH:mm:ss.SSSXXX',
                  ]),
                helperText: error?.[name]?.message as string,
                onFocus: () => {
                  if (dateRef?.current?.click) dateRef.current.click()
                },
                onBlur: (ev) => {
                  if (ev?.target?.value?.includes('0000')) {
                    onChange(null)
                  }
                },
              },

              field: { clearable: true, onClear: () => onChange(null) },
              popper: { sx: { zIndex: 1301 }, ...(placement && { placement }) },
            }}
          />
        </LocalizationProvider>
      )}
    </>
  )
}

export default MUIDateTimePicker
